/* Branded PDF export for a user guide.

   Reuses the report chrome (startReport / finishReport in reportPdf.ts) so the guide carries the
   same letterhead, running head and footer as the deliverables — one look across everything the
   app prints. Guides are not project-scoped, so the chrome is fed a synthetic "project" whose name
   is the guide title and a metaRows override (Version / Updated / Applies to) in place of the
   report's Address / Prepared by / Report date. */

import jsPDF from 'jspdf';
import { Project } from '../models/types';
import { Guide, GuideBlock } from '../content/guideTypes';
import { stripInline } from '../content/inline';
import { startReport, finishReport, LogoInput } from './reportPdf';

type Ctx = ReturnType<typeof startReport>;
type RGB = [number, number, number];

const SLATE: RGB = [38, 50, 56];
const MUTED: RGB = [140, 140, 140];

/* Callout tints, matching the memo/report status palette so a note reads the same on paper as it
   does on screen. */
type CalloutTone = 'info' | 'warning' | 'tip';
const TONE: Record<CalloutTone, { bg: RGB; bar: RGB; label: string }> = {
  info: { bg: [236, 239, 241], bar: [56, 142, 60], label: 'NOTE' },
  warning: { bg: [253, 236, 234], bar: [200, 60, 50], label: 'IMPORTANT' },
  tip: { bg: [255, 248, 225], bar: [245, 166, 35], label: 'TIP' },
};

const LINE_H = 4.2;

export interface GuidePdfOptions {
  logo: LogoInput;
}

/** Suggested download name for a guide PDF. */
export const guideFileName = (guide: Guide): string =>
  `${guide.title.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-') || 'MEP-Tools'}-Guide.pdf`;

function bulletList(c: Ctx, items: string[], ordered: boolean): void {
  const indent = 6;
  items.forEach((item, i) => {
    c.doc.setFontSize(9); c.doc.setFont('helvetica', 'normal'); c.ink(SLATE);
    const lines = c.wrap(stripInline(item), c.cw - indent);
    c.checkPage(lines.length * LINE_H + 2);
    c.write(ordered ? `${i + 1}.` : '•', c.m, c.y);
    c.doc.text(lines, c.m + indent, c.y);
    c.y += lines.length * LINE_H + 1.5;
  });
  c.y += 1.5;
}

function calloutBlock(c: Ctx, tone: CalloutTone, title: string | undefined, text: string): void {
  const t = TONE[tone];
  const bar = 1.6;
  const padX = 4;
  const padY = 3;
  const tx = c.m + bar + padX;
  const innerW = c.cw - bar - padX * 2;

  c.doc.setFontSize(9);
  const heading = title ? `${t.label}  ·  ${title}` : t.label;
  const headingLines = c.wrap(heading, innerW);
  const bodyLines = c.wrap(stripInline(text), innerW);
  const height = padY * 2 + (headingLines.length + bodyLines.length) * LINE_H;

  c.checkPage(height + 5);
  const top = c.y - 3.2;
  c.fill(t.bg); c.doc.rect(c.m, top, c.cw, height, 'F');
  c.fill(t.bar); c.doc.rect(c.m, top, bar, height, 'F');

  let y = top + padY + 2.6;
  c.ink(t.bar); c.doc.setFont('helvetica', 'bold');
  c.doc.text(headingLines, tx, y);
  y += headingLines.length * LINE_H;
  c.ink(SLATE); c.doc.setFont('helvetica', 'normal');
  c.doc.text(bodyLines, tx, y);

  c.y = top + height + 5;
}

function renderBlock(c: Ctx, block: GuideBlock): void {
  switch (block.kind) {
    case 'subheading':
      c.subHeading(block.text, 11);
      break;
    case 'paragraph':
      c.body(stripInline(block.text), 9.5);
      c.y += 1.5;
      break;
    case 'bullets':
      bulletList(c, block.items, false);
      break;
    case 'steps':
      bulletList(c, block.items, true);
      break;
    case 'table':
      c.table({
        startY: c.y,
        head: [block.columns],
        body: block.rows.map(r => r.map(stripInline)),
      });
      c.y += 3;
      if (block.caption) {
        c.doc.setFontSize(8); c.doc.setFont('helvetica', 'italic'); c.ink(MUTED);
        c.write(block.caption, c.m, c.y);
        c.y += 5;
      }
      break;
    case 'callout':
      calloutBlock(c, block.tone, block.title, block.text);
      break;
  }
}

export function buildGuidePdf(guide: Guide, { logo }: GuidePdfOptions): jsPDF {
  // The chrome only reads project.name (letterhead title + footer), so a minimal object is safe.
  const project = { name: guide.title, address: '' } as unknown as Project;

  const c = startReport({
    logo,
    project,
    preparedBy: 'Sustainability-Pros',
    kicker: 'MEP TOOLS USER GUIDE',
    metaRows: [
      ['Version', guide.version],
      ['Updated', guide.updated],
      ['Applies to', `MEP Tools — ${guide.title}`],
    ],
  });

  c.body(stripInline(guide.intro), 10);
  c.y += 4;

  guide.sections.forEach(section => {
    c.sectionHeading(section.title);
    section.blocks.forEach(block => renderBlock(c, block));
    c.y += 4;
  });

  finishReport(c, project);
  return c.doc;
}
